'use client';

import { useTranslation } from 'next-i18next';

import { useCallback, useEffect, useState } from 'react';

import { Box, Button, GridItem, HStack, Link, Menu, Text, VStack } from '@chakra-ui/react';

import { urlHelpers } from '@nocowanie/core/helpers';
import { Avatar } from '@nocowanie/common-ui/components/atoms/avatar';
import { GridWrapper } from '@nocowanie/common-ui/components/atoms/grid-wrapper';
import { IconsLinear } from '@nocowanie/icons/linear';

import { Navigation, NavigationMobile } from '@app/components/organisms/navigation';
import { MarketingBanner, MobileAppSmartBanner } from '@app/esi-components';
import { getDashboardLink, logOutUser } from '@app/helpers/navigation.helpers';
import { useUserProfile } from '@app/hooks/useUserProfile';
import { NavGroupModel } from '@app/models/navigation.model';

import { useAppHeaderHook } from './app-header.hook';
import { AppHeaderProps } from './app-header.props';
import { AppHeaderLogo } from './app-header-logo';

import staticData from '../../../assets/data/static-data.json';
import navLinks from '../navigation/navigation-links.json';

export const AppHeader = ({
    isSimplified = true,
    isHomePage = false,
    bannerData = null,
}: AppHeaderProps) => {
    const { t } = useTranslation('common', {
        keyPrefix: 'appHeader',
    });
    const { isMobileRes } = useAppHeaderHook();
    const isOldMobileVariant = isHomePage && isMobileRes;
    const { user } = useUserProfile();
    const [isProfileOpen, setIsProfileOpen] = useState(false);

    const handleLogout = useCallback(() => {
        logOutUser();
    }, []);

    const handleCloseProfile = useCallback(() => {
        setIsProfileOpen(false);
    }, []);

    const handleOpenProfile = useCallback(() => {
        setIsProfileOpen(true);
    }, []);

    useEffect(() => {
        if (!isProfileOpen) {
            return;
        }

        window.addEventListener('scroll', handleCloseProfile);

        return () => {
            window.removeEventListener('scroll', handleCloseProfile);
        };
    }, [handleCloseProfile, isProfileOpen]);

    const links = navLinks as NavGroupModel[];

    const ContactInfo = () => (
        <>
            <Box mr={2}>
                <IconsLinear.Call
                    mr={2}
                    fontSize={'xl'}
                    css={{
                        '--icon-stroke': 1.25,
                    }}
                />
                <Text
                    as={'span'}
                    hideBelow={'xl'}
                    fontSize={'md'}
                    alignSelf={'flex-end'}
                    dangerouslySetInnerHTML={{ __html: t('callUs') }}
                />
            </Box>
            <VStack align={'flex-start'} gap={0} hideBelow={'lg'} mr={4}>
                <Link
                    href={urlHelpers.getPhoneNoHref(staticData.navigation.hotline)}
                    variant={'plain'}
                    colorPalette={'gray'}
                    fontSize={'lg'}
                    fontWeight={'bold'}
                >
                    {staticData.navigation.hotline}
                </Link>
                <Text as={'span'} fontSize={'13px'} color={'gray.600'}>
                    {staticData.navigation.openingDays} {staticData.navigation.openingHours}
                    {' · '}
                    {staticData.navigation.openingDaysWeekend}{' '}
                    {staticData.navigation.openingHoursWeekend}
                </Text>
            </VStack>
        </>
    );

    const HeaderRightSection = () =>
        !isMobileRes ? (
            user ? (
                <HStack>
                    <HStack gap={'gutterWidth'}>
                        <ContactInfo />
                    </HStack>
                    <Menu.Root
                        open={isProfileOpen}
                        onOpenChange={details =>
                            details.open ? handleOpenProfile() : handleCloseProfile()
                        }
                    >
                        <Menu.Trigger
                            as={Button}
                            _hover={{
                                bg: 'transparent',
                            }}
                            _expanded={{
                                bg: 'transparent',
                            }}
                            title={user.firstName || user.lastName || t('userMenu.defaultName')}
                            alignItems={'center'}
                        >
                            <Avatar src={user.avatar ?? undefined} />
                            <IconsLinear.ArrowDown2 fontSize={'sm'} />
                        </Menu.Trigger>
                        <Menu.Positioner>
                            <Menu.Content zIndex={5} color={'secondary.solid'} fontSize={'sm'}>
                                <Menu.Item
                                    _hover={{
                                        bg: 'secondary.50',
                                        textDecoration: 'none',
                                    }}
                                    _focus={{
                                        bg: 'secondary.50',
                                    }}
                                    asChild
                                    value="dashboard"
                                    cursor={'pointer'}
                                >
                                    <a href={getDashboardLink(user)}>{t('userMenu.goToPanel')}</a>
                                </Menu.Item>
                                <Menu.Item
                                    onClick={handleLogout}
                                    _hover={{
                                        bg: 'secondary.50',
                                    }}
                                    _focus={{
                                        bg: 'secondary.50',
                                    }}
                                    value="logout"
                                    cursor={'pointer'}
                                >
                                    {t('userMenu.logOut')}
                                </Menu.Item>
                            </Menu.Content>
                        </Menu.Positioner>
                    </Menu.Root>
                </HStack>
            ) : (
                <HStack gap={'gutterWidth'}>
                    <ContactInfo />
                    <Button
                        asChild
                        colorPalette={'secondary'}
                        variant={'outline'}
                        fontWeight={'600'}
                        px={6}
                        py={4}
                    >
                        <a href={links[0].navLinks[0].url ?? '/'}>
                            <IconsLinear.User />
                            {t('cta.logIn')}
                        </a>
                    </Button>
                </HStack>
            )
        ) : null;

    return (
        <>
            <MobileAppSmartBanner />
            {isHomePage && bannerData ? <MarketingBanner bannerData={bannerData} /> : null}
            <Box
                as={'header'}
                py={isOldMobileVariant ? 0 : 2}
                borderBottom={'1px solid'}
                borderBottomColor={'gray.200'}
            >
                <GridWrapper
                    px={{
                        base: 'gutterWidth',
                        md: 'gutterWidthDesktop',
                        lg2: 8,
                    }}
                    maxW={'full'}
                >
                    {isSimplified ? (
                        <AppHeaderLogo />
                    ) : (
                        <HStack as={GridItem} gridColumn={'1 / -1'} justify={'space-between'}>
                            <HStack gap={isMobileRes ? 0 : 'gutterWidth'}>
                                {isMobileRes ? (
                                    <NavigationMobile
                                        menuLabel={t('menu.menuLabel')}
                                        closeLabel={t('menu.closeLabel')}
                                        user={user}
                                    />
                                ) : null}
                                <AppHeaderLogo />
                                {!isMobileRes ? (
                                    <Navigation triggerText={t('menu.planYourStay')} />
                                ) : null}
                            </HStack>
                            <HeaderRightSection />
                        </HStack>
                    )}
                </GridWrapper>
            </Box>
        </>
    );
};
